import {ActivityHeader} from '@github-ui/commenting/ActivityHeader'
import {VerifiedHTMLDiv} from '@github-ui/safe-html/VerifiedHTML'
import type {ReviewSummary, ReviewSummaryState} from '../../page-data/payloads/review-summaries'
import {CheckIcon, ChevronRightIcon, DiffIcon, EyeIcon} from '@primer/octicons-react'
import {type SelectionContext, selectQuoteFromComment} from '@github-ui/commenting/quotes'
import {useRef, useCallback} from 'react'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import CircleOcticon from '@github-ui/circle-octicon'
import {Button} from '@primer/react'
import {useCombinedCurrentUser} from '../../stores/selectors'

interface ReviewSummaryItemProps {
  reviewSummary: ReviewSummary
  pullRequestAuthorLogin: string
  canReact: boolean
  onQuoteReply: (quotedText: string) => void
}

function ReviewStateIcon({state}: {state: ReviewSummaryState}) {
  switch (state) {
    case 'APPROVED':
      return (
        <CircleOcticon
          icon={() => <CheckIcon size={16} />}
          className="bgColor-success-emphasis fgColor-onEmphasis"
          size={32}
        />
      )
    case 'CHANGES_REQUESTED':
      return (
        <CircleOcticon
          icon={() => <DiffIcon size={16} />}
          className="bgColor-danger-emphasis fgColor-onEmphasis"
          size={32}
        />
      )
    case 'COMMENTED':
      return <CircleOcticon icon={() => <EyeIcon size={16} />} className="bgColor-muted fgColor-onEmphasis" size={32} />
    case 'DISMISSED':
    default:
      return null
  }
}

export function ReviewSummaryItem({
  reviewSummary,
  pullRequestAuthorLogin,
  onQuoteReply,
}: ReviewSummaryItemProps): React.ReactElement {
  const commentRef = useRef<HTMLDivElement>(null)
  const currentUser = useCombinedCurrentUser()

  const hasBody = reviewSummary.body.trim().length > 0

  const handleQuoteClick = useCallback(() => {
    if (!hasBody) return
    const current = ssrSafeWindow?.getSelection()
    let currentSelection: SelectionContext | null = null

    if (current && current.anchorNode && current.rangeCount > 0) {
      currentSelection = {
        anchorNode: current.anchorNode,
        range: current.getRangeAt(0),
      }
    }

    const quotedText = selectQuoteFromComment(commentRef.current, currentSelection, reviewSummary.body)
    onQuoteReply(quotedText ?? reviewSummary.body)
  }, [hasBody, onQuoteReply, reviewSummary.body])

  return (
    <div id={`review-summary-${reviewSummary.id}`} ref={commentRef} className="border rounded-2">
      <div className="pt-2 tmp-px-3 d-flex flex-items-center flex-justify-between">
        <div className="d-flex flex-items-center gap-2 min-width-0 flex-1">
          <ActivityHeader
            avatarUrl={reviewSummary.authorAvatarUrl || ''}
            comment={{
              id: reviewSummary.id,
              authorAssociation: reviewSummary.authorAssociation,
              createdAt: reviewSummary.submittedAt,
              isHidden: false,
              minimizedReason: null,
              repository: reviewSummary.repository,
              url: reviewSummary.url,
            }}
            commentAuthorLogin={reviewSummary.authorLogin}
            commentSubjectType="pull request"
            commentSubjectAuthorLogin={pullRequestAuthorLogin}
            forceInlineAvatar
            isMinimized={false}
            containerStyle={{}}
            tooltipDirection="sw"
          />
          <ReviewStateIcon state={reviewSummary.state} />
        </div>
      </div>
      {hasBody && (
        <div className="tmp-px-3 py-2">
          <VerifiedHTMLDiv className="markdown-body mt-1 f5" html={reviewSummary.bodyHtml} />
          {/* TODO: add an endpoint for applying reactions to review summary comments*/}
          {currentUser.canComment && (
            <Button
              className="mt-2 ml-n2"
              onClick={handleQuoteClick}
              size="small"
              trailingVisual={ChevronRightIcon}
              variant="invisible"
            >
              Quote reply
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
